import React from 'react';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const ContactSettingsForm = ({ formData, errors, onChange }) => {
  const handleOfficeChange = (index, field, value) => {
    const updated = [...formData?.offices];
    updated[index] = { ...updated?.[index], [field]: value };
    onChange('offices', updated);
  };

  const handleAddOffice = () => {
    onChange('offices', [...formData?.offices, { location: '', address: '', phone: '' }]);
  };

  const handleRemoveOffice = (index) => {
    const updated = formData?.offices?.filter((_, i) => i !== index);
    onChange('offices', updated);
  };

  const handleHRContactChange = (index, field, value) => {
    const updated = [...formData?.hrContacts];
    updated[index] = { ...updated?.[index], [field]: value };
    onChange('hrContacts', updated);
  };

  const handleAddHRContact = () => {
    onChange('hrContacts', [...formData?.hrContacts, { name: '', email: '', phone: '', designation: '' }]);
  };

  const handleRemoveHRContact = (index) => {
    const updated = formData?.hrContacts?.filter((_, i) => i !== index);
    onChange('hrContacts', updated);
  };

  const handleSocialMediaChange = (platform, value) => {
    onChange('socialMedia', { ...formData?.socialMedia, [platform]: value });
  };

  const handlePrivacyChange = (field, value) => {
    onChange('privacySettings', { ...formData?.privacySettings, [field]: value });
  };

  const handleCommunicationChange = (field, value) => {
    onChange('communicationPreferences', { ...formData?.communicationPreferences, [field]: value });
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-card-foreground">Office Locations</h3>
          <Button variant="outline" size="sm" onClick={handleAddOffice} iconName="Plus" iconPosition="left">
            Add Office
          </Button>
        </div>
        <div className="space-y-4">
          {formData?.offices?.map((office, index) => (
            <div key={index} className="p-4 bg-muted/50 rounded-lg border border-border">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-card-foreground">Office {index + 1}</h4>
                {formData?.offices?.length > 1 && (
                  <button
                    onClick={() => handleRemoveOffice(index)}
                    className="text-error hover:text-error/80 transition-smooth"
                    aria-label="Remove office"
                  >
                    <Icon name="Trash2" size={16} />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input
                  label="Location"
                  type="text"
                  placeholder="e.g., Mumbai, Maharashtra"
                  value={office?.location}
                  onChange={(e) => handleOfficeChange(index, 'location', e?.target?.value)}
                  required
                />
                <Input
                  label="Phone"
                  type="tel"
                  placeholder="+91 98765 43210"
                  value={office?.phone}
                  onChange={(e) => handleOfficeChange(index, 'phone', e?.target?.value)}
                />
              </div>
              <Input
                label="Address"
                type="text"
                placeholder="Complete office address"
                value={office?.address}
                onChange={(e) => handleOfficeChange(index, 'address', e?.target?.value)}
                className="mt-3"
              />
            </div>
          ))}
        </div>
        {errors?.offices && <p className="text-sm text-destructive mt-2">{errors?.offices}</p>}
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-card-foreground">HR Contacts</h3>
          <Button variant="outline" size="sm" onClick={handleAddHRContact} iconName="Plus" iconPosition="left">
            Add Contact
          </Button>
        </div>
        <div className="space-y-4">
          {formData?.hrContacts?.map((contact, index) => (
            <div key={index} className="p-4 bg-muted/50 rounded-lg border border-border">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-card-foreground">Contact {index + 1}</h4>
                {formData?.hrContacts?.length > 1 && (
                  <button
                    onClick={() => handleRemoveHRContact(index)}
                    className="text-error hover:text-error/80 transition-smooth"
                    aria-label="Remove contact"
                  >
                    <Icon name="Trash2" size={16} />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input
                  label="Name"
                  type="text"
                  placeholder="HR Manager name"
                  value={contact?.name}
                  onChange={(e) => handleHRContactChange(index, 'name', e?.target?.value)}
                />
                <Input
                  label="Designation"
                  type="text"
                  placeholder="e.g., HR Manager"
                  value={contact?.designation}
                  onChange={(e) => handleHRContactChange(index, 'designation', e?.target?.value)}
                />
                <Input
                  label="Email"
                  type="email"
                  placeholder="hr@company.com"
                  value={contact?.email}
                  onChange={(e) => handleHRContactChange(index, 'email', e?.target?.value)}
                  required
                />
                <Input
                  label="Phone"
                  type="tel"
                  placeholder="+91 98765 43210"
                  value={contact?.phone}
                  onChange={(e) => handleHRContactChange(index, 'phone', e?.target?.value)}
                />
              </div>
            </div>
          ))}
        </div>
        {errors?.hrContacts && <p className="text-sm text-destructive mt-2">{errors?.hrContacts}</p>}
      </div>

      <div>
        <h3 className="text-lg font-semibold text-card-foreground mb-4">Social Media Links</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="LinkedIn"
            type="url"
            placeholder="https://linkedin.com/company/..."
            value={formData?.socialMedia?.linkedin}
            onChange={(e) => handleSocialMediaChange('linkedin', e?.target?.value)}
          />
          <Input
            label="Twitter"
            type="url"
            placeholder="https://twitter.com/..."
            value={formData?.socialMedia?.twitter}
            onChange={(e) => handleSocialMediaChange('twitter', e?.target?.value)}
          />
          <Input
            label="Facebook"
            type="url"
            placeholder="https://facebook.com/..."
            value={formData?.socialMedia?.facebook}
            onChange={(e) => handleSocialMediaChange('facebook', e?.target?.value)}
          />
          <Input
            label="Instagram"
            type="url"
            placeholder="https://instagram.com/..."
            value={formData?.socialMedia?.instagram}
            onChange={(e) => handleSocialMediaChange('instagram', e?.target?.value)}
          />
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-card-foreground mb-4">Privacy & Communication Settings</h3>
        <div className="space-y-3">
          <Checkbox
            id="profile-visibility"
            checked={formData?.privacySettings?.profileVisibility === 'public'}
            onChange={(e) => handlePrivacyChange('profileVisibility', e?.target?.checked ? 'public' : 'private')}
            label="Make profile visible to all candidates"
            description="Your company profile will be discoverable in search results"
          />
          <Checkbox
            id="show-contact"
            checked={formData?.privacySettings?.showContactInfo}
            onChange={(e) => handlePrivacyChange('showContactInfo', e?.target?.checked)}
            label="Display contact information publicly"
            description="Candidates can see your office locations and HR contacts"
          />
          <Checkbox
            id="email-notifications"
            checked={formData?.communicationPreferences?.emailNotifications}
            onChange={(e) => handleCommunicationChange('emailNotifications', e?.target?.checked)}
            label="Receive email notifications for new applications"
          />
          <Checkbox
            id="sms-notifications"
            checked={formData?.communicationPreferences?.smsNotifications}
            onChange={(e) => handleCommunicationChange('smsNotifications', e?.target?.checked)}
            label="Receive SMS notifications for urgent updates"
          />
        </div>
      </div>
    </div>
  );
};

export default ContactSettingsForm;